/**
 * Finalization validator — enforces RI EMS Compliance Spec §§7, 8, 10, 12
 *
 * Returns a list of errors + warnings that prevent/flag final QA sign-off.
 * Errors are BLOCKING. Warnings are informational.
 */

import { db } from '@/offline/db';
import { RI_PROTOCOLS, type Protocol, type ProtocolRequirement } from '@moquintrend/protocol-engine';
import type { EPCR, Incident, VitalSet } from '@/types';
import { isDemoModeActive } from './demo-mode-store';

export interface ValidationIssue {
  id: string;
  severity: 'error' | 'warning';
  category: 'protocol_requirement' | 'missing_data' | 'placeholder_data' | 'vital_sets' | 'transfer_of_care' | 'signature' | 'attachment' | 'narrative' | 'transmission';
  field: string;
  message: string;
  cite?: string;
  /** Hash path to jump to in the UI, e.g., "#patient", "#summary", "#protocol-compliance" */
  anchor?: string;
}

export interface ValidationResult {
  canFinalize: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
}

// Placeholder patterns that users try to use to bypass validation
const FAKE_NAME_PATTERNS = [
  /^jane doe$/i, /^john doe$/i, /^unknown$/i, /^test$/i,
  /^n\/?a$/i, /^none$/i, /^xxx$/i, /^\?+$/,
];
const FAKE_DOB_PATTERNS = [
  /^1900-01-01$/, /^1901-01-01$/, /^0000-00-00$/, /^1\/1\/1900$/,
];

export async function validateForFinalization(epcr: EPCR): Promise<ValidationResult> {
  const errors: ValidationIssue[] = [];
  const warnings: ValidationIssue[] = [];

  const [vitals, procedures, medications, narratives, signatures, attachments, incident] = await Promise.all([
    db.vital_sets.where('epcr_id').equals(epcr.id).toArray(),
    db.procedures.where('epcr_id').equals(epcr.id).toArray(),
    db.medications_given.where('epcr_id').equals(epcr.id).toArray(),
    db.narratives.where('epcr_id').equals(epcr.id).toArray(),
    db.signatures.where('epcr_id').equals(epcr.id).toArray(),
    db.attachments.where('epcr_id').equals(epcr.id).toArray(),
    db.incidents.get(epcr.incident_id),
  ]);

  // --- §12.2: Core patient identifier checks (no fake placeholders) ---
  if (epcr.patient_last_name && FAKE_NAME_PATTERNS.some(re => re.test(epcr.patient_last_name!))) {
    errors.push({
      id: 'fake_last_name', severity: 'error', category: 'placeholder_data',
      field: 'patient_last_name',
      message: `Patient last name appears to be a placeholder ("${epcr.patient_last_name}"). Enter real name or leave blank and use addendum workflow when available.`,
      cite: 'Compliance Spec §12.2', anchor: '#patient',
    });
  }
  if (epcr.patient_dob && FAKE_DOB_PATTERNS.some(re => re.test(epcr.patient_dob!))) {
    errors.push({
      id: 'fake_dob', severity: 'error', category: 'placeholder_data',
      field: 'patient_dob',
      message: `Patient DOB looks like a placeholder (${epcr.patient_dob}). Leave blank rather than using a fake date.`,
      cite: 'Compliance Spec §12.2', anchor: '#patient',
    });
  }

  // --- §8.1: Vital signs minimum count ---
  const transportDecisions = ['transport_this_unit', 'transfer_other_unit'];
  const isTransport = epcr.transport_decision && transportDecisions.includes(epcr.transport_decision);
  const completeVitalSets = vitals.filter(v => isCompleteVitalSet(v)).length;

  if (isTransport && completeVitalSets < 2) {
    errors.push({
      id: 'vitals_min_count', severity: 'error', category: 'vital_sets',
      field: 'vital_sets',
      message: `Transported patients require at least 2 complete vital sign sets. Currently documented: ${completeVitalSets} complete, ${vitals.length} total.`,
      cite: 'RI Protocol 1.02 §9 — 2 complete vital sets required for transported patients',
      anchor: '#vitals',
    });
  } else if (epcr.transport_decision && !isTransport && completeVitalSets < 1) {
    errors.push({
      id: 'vitals_min_count', severity: 'error', category: 'vital_sets',
      field: 'vital_sets',
      message: `Non-transport contacts require at least 1 complete vital sign set.`,
      cite: 'RI Protocol 1.02 §9',
      anchor: '#vitals',
    });
  }

  // --- §7: Condition-specific protocol requirements ---
  const activeProtocolIds = epcr.protocol_state?.active_protocol_ids ?? [];
  const activeProtocols: Protocol[] = activeProtocolIds
    .map(id => RI_PROTOCOLS.find(p => p.id === id))
    .filter((p): p is Protocol => !!p);

  // --- Protocol steps that are expected to be completed but weren't:
  //     require an explicit "skip reason" / deviation note before finalize.
  //     "Expected" = step is a procedure, medication, or monitor AND the
  //     step's requires* conditions are met (it's reachable in the current pathway).
  const deviations = epcr.protocol_state?.deviations ?? [];
  const completedSet = new Set(
    (epcr.protocol_state?.completed_steps ?? []).map((c) => `${c.protocol_id}:${c.step_order}`),
  );
  for (const protocol of activeProtocols) {
    const decisionsByStep = new Map(
      (epcr.protocol_state?.decisions ?? [])
        .filter((d) => d.protocol_id === protocol.id)
        .map((d) => [d.step_order, d.choice] as const),
    );
    const completedInProtocol = new Set(
      (epcr.protocol_state?.completed_steps ?? [])
        .filter((c) => c.protocol_id === protocol.id)
        .map((c) => c.step_order),
    );
    for (const step of protocol.steps) {
      if (step.type !== 'procedure' && step.type !== 'medication' && step.type !== 'monitor') continue;

      // Skip if not reachable (gated by prior requires*)
      if (step.requiresStepsCompleted?.length) {
        const allDone = step.requiresStepsCompleted.every((n) => completedInProtocol.has(n));
        if (!allDone) continue;
      }
      if (step.requiresDecision?.length) {
        const allMet = step.requiresDecision.every(
          (r) => decisionsByStep.get(r.stepOrder) === r.choice,
        );
        if (!allMet) continue;
      }

      // If step is completed, no explanation needed.
      if (completedSet.has(`${protocol.id}:${step.order}`)) continue;

      // Step was reachable but not completed — require a deviation reason.
      const hasDeviationNote = deviations.some(
        (d) => d.protocol_id === protocol.id && d.step_order === step.order && d.reason.trim().length > 0,
      );
      if (!hasDeviationNote) {
        errors.push({
          id: `${protocol.id}:step-${step.order}-no-reason`,
          severity: 'error',
          category: 'protocol_requirement',
          field: `protocol_step_${protocol.id}_${step.order}`,
          message: `${protocol.name}: step ${step.order} (${step.description}) was not completed — document a reason (contraindication, patient refusal, clinical judgment) before finalization`,
          cite: `${protocol.source} v${protocol.version} — skip reasons required`,
          anchor: '#protocol-requirements',
        });
      }
    }
  }

  for (const protocol of activeProtocols) {
    if (!protocol.requirements) continue;
    for (const req of protocol.requirements) {
      // Conditional requirements: only check if the gating decision was made
      if (req.conditionalOn) {
        const decision = epcr.protocol_state?.decisions.find(
          d => d.protocol_id === protocol.id && d.step_order === req.conditionalOn!.stepOrder,
        );
        if (!decision || decision.choice !== req.conditionalOn.choice) continue;
      }

      const entry = epcr.compliance_fields?.[req.key];
      const hasValue = entry && entry.value !== null && entry.value !== '' && entry.value !== undefined;

      // Attachment requirements check the attachments table
      if (req.type === 'attachment') {
        const hasAttachment = attachments.some(a => a.category === req.attachmentCategory);
        if (!hasAttachment) {
          (req.required ? errors : warnings).push({
            id: `${protocol.id}:${req.key}`,
            severity: req.required ? 'error' : 'warning',
            category: 'attachment',
            field: req.key,
            message: `${protocol.name}: ${req.label} — attach ${req.attachmentCategory} file`,
            cite: req.cite,
            anchor: '#attachments',
          });
        }
        continue;
      }

      if (!hasValue) {
        (req.required ? errors : warnings).push({
          id: `${protocol.id}:${req.key}`,
          severity: req.required ? 'error' : 'warning',
          category: 'protocol_requirement',
          field: req.key,
          message: `${protocol.name}: ${req.label} is required`,
          cite: req.cite,
          anchor: '#protocol-requirements',
        });
      }
    }
  }

  // --- Core clinical fields ---
  if (!epcr.chief_complaint) {
    errors.push({
      id: 'no_chief_complaint', severity: 'error', category: 'missing_data',
      field: 'chief_complaint',
      message: 'Chief complaint is required',
      cite: 'RI Protocol 1.02 §6', anchor: '#dispatch',
    });
  }
  if (!epcr.primary_impression) {
    errors.push({
      id: 'no_primary_impression', severity: 'error', category: 'missing_data',
      field: 'primary_impression',
      message: 'Primary impression is required',
      cite: 'RI Protocol 1.02 §6', anchor: '#dispatch',
    });
  }
  if (!epcr.transport_decision) {
    errors.push({
      id: 'no_disposition', severity: 'error', category: 'missing_data',
      field: 'transport_decision',
      message: 'Transport decision / disposition is required',
      cite: 'RI Protocol 1.02', anchor: '#disposition',
    });
  }
  if (epcr.allergies.length === 0) {
    warnings.push({
      id: 'no_allergies', severity: 'warning', category: 'missing_data',
      field: 'allergies',
      message: 'Allergies not documented (use NKA or NKDA if unknown/none)',
      cite: 'RI Protocol 1.02 §6', anchor: '#patient',
    });
  }

  // --- §9: Narrative present and approved ---
  if (narratives.length === 0 || !narratives[0].generated_text) {
    errors.push({
      id: 'no_narrative', severity: 'error', category: 'narrative',
      field: 'narrative',
      message: 'Clinical narrative must be generated before finalization',
      cite: 'RI Protocol 1.02 §13', anchor: '#narrative',
    });
  } else if (!epcr.narrative_approved) {
    errors.push({
      id: 'narrative_not_approved', severity: 'error', category: 'narrative',
      field: 'narrative',
      message: 'Provider must approve the clinical narrative',
      cite: 'Internal QA', anchor: '#narrative',
    });
  }

  // --- §10: Transfer of Care (only for transports) ---
  if (isTransport) {
    if (!epcr.destination_hospital) {
      errors.push({
        id: 'no_destination', severity: 'error', category: 'transfer_of_care',
        field: 'destination_hospital',
        message: 'Receiving facility is required for transports',
        cite: 'RI Protocol 1.02 §23', anchor: '#disposition',
      });
    }

    const receivingSig = signatures.find(s => s.signer_role === 'receiving_clinician');
    if (!receivingSig) {
      errors.push({
        id: 'no_receiving_signature', severity: 'error', category: 'signature',
        field: 'receiving_clinician_signature',
        message: 'Receiving clinician signature required',
        cite: 'RI Protocol 1.02 §11 — transfer of care signature must be obtained',
        anchor: '#toc',
      });
    } else if (!receivingSig.signer_licensure) {
      errors.push({
        id: 'receiving_sig_no_licensure', severity: 'error', category: 'signature',
        field: 'receiving_clinician_licensure',
        message: 'Receiving clinician licensure (RN, APRN, PA, MD, DO) is required',
        cite: 'RI Protocol 1.02 §11', anchor: '#toc',
      });
    }

    if (!epcr.toc_completed) {
      warnings.push({
        id: 'toc_not_marked_complete', severity: 'warning', category: 'transfer_of_care',
        field: 'toc_completed',
        message: 'Mark transfer of care as completed once handoff is done',
        cite: 'RI Protocol 1.02 §11', anchor: '#toc',
      });
    }
  }

  // --- Refusal requires patient signature ---
  const isRefusal = epcr.transport_decision === 'refusal_care' || epcr.transport_decision === 'refusal_transport';
  if (isRefusal) {
    const patientSig = signatures.find(s => s.signer_role === 'patient' || s.signer_role === 'guardian');
    if (!patientSig) {
      errors.push({
        id: 'no_refusal_signature', severity: 'error', category: 'signature',
        field: 'patient_signature',
        message: 'Patient (or guardian) signature required for refusal',
        cite: 'RI Protocol 6.04 — Refusal of Patient Care or Transport',
        anchor: '#disposition',
      });
    }
    const witnessSig = signatures.find(s => s.signer_role === 'witness');
    if (!witnessSig) {
      warnings.push({
        id: 'no_refusal_witness', severity: 'warning', category: 'signature',
        field: 'witness_signature',
        message: 'Witness signature recommended for refusal',
        cite: 'RI Protocol 6.04', anchor: '#disposition',
      });
    }
  }

  // --- Unit / Agency sanity ---
  if (!epcr.unit_id) {
    warnings.push({
      id: 'no_unit', severity: 'warning', category: 'missing_data',
      field: 'unit_id',
      message: 'Responding unit not identified',
      cite: 'Compliance Spec §3.1',
    });
  }

  return {
    // Demo mode: every blocker becomes a warning + canFinalize is
    // always true. Lets a presenter click Finalize / Approve / Lock
    // during a walkthrough without entering real demographics or
    // filling in every required field. Reports created this way are
    // tagged .demo_mode at save-time so downstream metrics can
    // exclude them.
    canFinalize: isDemoModeActive() ? true : errors.length === 0,
    errors: isDemoModeActive() ? [] : errors,
    warnings: isDemoModeActive() ? [...warnings, ...errors] : warnings,
  };
}

/**
 * A vital set is "complete" if it has at minimum BP, pulse, respirations, and SpO2
 * (RI Protocol 1.02 §9).
 */
function isCompleteVitalSet(v: VitalSet): boolean {
  return !!(
    v.bp_systolic && v.bp_diastolic &&
    v.heart_rate &&
    v.respiratory_rate &&
    v.spo2
  );
}
